import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from '@store';
import {profileAPI} from '@api/methods';
import {IDLE, PENDING} from '@constants/loadingState';
import {IProfile} from '@types';

interface ProfileState {
  loading: string;
  profile: IProfile | null;
}

const initialState: ProfileState = {
  loading: IDLE,
  profile: null,
};

export const fetchProfile = createAsyncThunk<IProfile, void, {state: RootState}>(
  'profile/getMe',
  async () => {
    return await profileAPI.getMe();
  },
);

export const saveProfile = createAsyncThunk<
  IProfile,
  {username?: string; email?: string; avatarFile?: File | null},
  {state: RootState}
>('profile/update', async ({username, email, avatarFile}) => {
  return await profileAPI.updateProfile({
    username,
    email,
    avatarFile: avatarFile || undefined,
  });
});

export const updatePassword = createAsyncThunk<
  any,
  {last_password: string; new_password: string},
  {state: RootState}
>('profile/updatePassword', async ({last_password, new_password}) => {
  return await profileAPI.updatePassword({last_password, new_password});
});

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    setProfile: (state, action: PayloadAction<IProfile | null>) => {
      state.profile = action.payload ?? null;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchProfile.pending, state => {
      state.loading = PENDING;
    });
    builder.addCase(fetchProfile.rejected, state => {
      state.loading = IDLE;
    });
    builder.addCase(fetchProfile.fulfilled, (state, {payload}) => {
      state.loading = IDLE;
      state.profile = payload ?? null;
    });
    builder.addCase(saveProfile.pending, state => {
      state.loading = PENDING;
    });
    builder.addCase(saveProfile.rejected, state => {
      state.loading = IDLE;
    });
    builder.addCase(saveProfile.fulfilled, (state, {payload}) => {
      state.loading = IDLE;
      state.profile = payload ?? state.profile;
    });
  },
});

export const selectProfileLoading = (state: RootState) => state.profile.loading;

export const {setProfile} = profileSlice.actions;
export default profileSlice.reducer;
